import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import TablerIconArrowLeft from '~icons/tabler/arrow-left';
import TablerIconHome from '~icons/tabler/home';

import { Container, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page } from '#/components/pages/page';
import fx from '#/fx';
import { Content } from '#/lib/content';
import { canonicalLinkTag, seo } from '#/lib/head';

import { BlogPostAuthor } from './-components/blog-post-author';

export const Route = createFileRoute('/_main/blog/posts/$postId')({
  loader: async ({ params }) => {
    const post = await fx.getBlogPost({
      data: {
        id: params.postId,
      },
    });

    return { post };
  },
  head: ({ loaderData, match }) => {
    return {
      meta: seo({
        title: loaderData?.post.seo.title ?? 'Blog Post',
        description: loaderData?.post.seo.description ?? 'A blog post from the Joggr team.',
        image: loaderData?.post.seo.image,
        url: match.pathname,
      }),
      links: [canonicalLinkTag(match.pathname)],
    };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  return (
    <Page>
      <Stack direction='column' spacing={1} alignItems='center' className='mt-8'>
        <Container maxWidth='md' className='px-2'>
          <Stack alignItems='center' justifyContent='center' spacing={2}>
            <Typography variant='caption'>
              {DateTime.fromJSDate(new Date(post.date)).toLocaleString(DateTime.DATE_SHORT)}
            </Typography>
            <div className='text-center mb-4'>
              <Typography variant='h2' component='h1' gutterBottom>
                {post.title}
              </Typography>
              <Typography className='text-gray-400'>{post.summary}</Typography>
            </div>
            <div>
              <BlogPostAuthor author={post.author} size='medium' />
            </div>
          </Stack>
        </Container>
        <Container maxWidth='lg' className='py-4'>
          <Img
            src={post.cover.src}
            alt={`Cover photo for post: ${post.title}`}
            height={post.cover.height}
            width={post.cover.width}
            location='static'
            className='rounded-lg'
            responsive
          />
        </Container>
        <Container maxWidth='md'>
          <Content content={post.content} format='mdx' styling='prose' />
        </Container>
      </Stack>
      <Container>
        <GlowCard
          border='top'
          glowHeight='8'
          className='min-h-32 w-full mt-16 md:w-2/3 lg:w-1/2 mx-auto'
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems='center'
            justifyContent='stretch'
          >
            <div className='w-full'>
              <ButtonLink
                fullWidth
                variant='outlined'
                to='/blog'
                startIcon={<TablerIconArrowLeft />}
              >
                Back to Blog
              </ButtonLink>
            </div>
            <div className='w-full'>
              <ButtonLink fullWidth variant='outlined' to='/' startIcon={<TablerIconHome />}>
                Go to Home
              </ButtonLink>
            </div>
          </Stack>
        </GlowCard>
      </Container>
    </Page>
  );
}
